import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { submitLead, formatApiError } from "@/lib/api";
import { trackEvent, getUtmParams } from "@/lib/analytics";

const PARTICIPANTS = ["10", "15", "20", "25", "30", "40", "50", "60", "80", "100+"];
const PACKAGES_OPTS = ["Mission", "Mission 24", "Mission Weekend", "Nevím"];
const BUDGETS = ["do 4 000 Kč", "4 000–6 000 Kč", "6 000–8 000 Kč", "8 000+ Kč"];

const inputCls = "w-full bg-surface border border-linestrong text-white text-sm px-3 py-2.5 placeholder:text-dim focus:outline-none focus:border-amber transition-colors";
const labelCls = "block text-xs font-mono uppercase tracking-[0.15em] text-mist mb-1.5";

const Field = ({ label, htmlFor, required, children }) => (
  <div>
    <label htmlFor={htmlFor} className={labelCls}>
      {label} {required && <span className="text-amber">*</span>}
    </label>
    {children}
  </div>
);

export const LeadForm = ({ heading = "PŘIPRAVÍME VÁM MISI", text }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    participants: "", preferredDate: "", originCity: "", packageInterest: "", budget: "",
    contactName: "", company: "", phone: "", email: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (!started) {
      setStarted(true);
      trackEvent("start_lead_form", { page: location.pathname });
    }
    if (k === "packageInterest") trackEvent("package_select", { package: v, page: location.pathname });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await submitLead({
        ...form,
        landingPage: location.pathname,
        source: "web",
        ...getUtmParams(),
      });
      trackEvent("submit_lead", { page: location.pathname, package: form.packageInterest, participants: form.participants });
      navigate("/dekujeme");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-surface border border-linestrong p-6 sm:p-10" data-testid="lead-form-section">
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">{heading}</h2>
      {text && <p className="text-mist mt-3 max-w-xl">{text}</p>}
      <form onSubmit={onSubmit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="lead-form">
        <Field label="Počet účastníků" htmlFor="lf-participants">
          <select id="lf-participants" value={form.participants} onChange={set("participants")} className={inputCls} data-testid="lead-form-participants-input">
            <option value="">Vyberte…</option>
            {PARTICIPANTS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Preferovaný termín" htmlFor="lf-date">
          <input id="lf-date" type="text" value={form.preferredDate} onChange={set("preferredDate")} placeholder="např. září 2026, čtvrtek–pátek" className={inputCls} data-testid="lead-form-date-input" />
        </Field>
        <Field label="Odkud přijedete" htmlFor="lf-origin">
          <input id="lf-origin" type="text" value={form.originCity} onChange={set("originCity")} placeholder="např. Praha" className={inputCls} data-testid="lead-form-origin-input" />
        </Field>
        <Field label="Balíček" htmlFor="lf-package">
          <select id="lf-package" value={form.packageInterest} onChange={set("packageInterest")} className={inputCls} data-testid="lead-form-package-select">
            <option value="">Vyberte…</option>
            {PACKAGES_OPTS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Budget na osobu" htmlFor="lf-budget">
          <select id="lf-budget" value={form.budget} onChange={set("budget")} className={inputCls} data-testid="lead-form-budget-select">
            <option value="">Vyberte…</option>
            {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>
        <Field label="Jméno" htmlFor="lf-name" required>
          <input id="lf-name" type="text" required value={form.contactName} onChange={set("contactName")} autoComplete="name" className={inputCls} data-testid="lead-form-name-input" />
        </Field>
        <Field label="Firma" htmlFor="lf-company" required>
          <input id="lf-company" type="text" required value={form.company} onChange={set("company")} autoComplete="organization" className={inputCls} data-testid="lead-form-company-input" />
        </Field>
        <Field label="Telefon" htmlFor="lf-phone">
          <input id="lf-phone" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" className={inputCls} data-testid="lead-form-phone-input" />
        </Field>
        <Field label="E-mail" htmlFor="lf-email" required>
          <input id="lf-email" type="email" required value={form.email} onChange={set("email")} autoComplete="email" className={inputCls} data-testid="lead-form-email-input" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Poznámka" htmlFor="lf-message">
            <textarea id="lf-message" rows={4} value={form.message} onChange={set("message")} placeholder="Např. složení týmu, dietní omezení, interní program…" className={inputCls} data-testid="lead-form-message-input" />
          </Field>
        </div>
        {error && <p className="sm:col-span-2 text-sm text-red-400" role="alert" data-testid="lead-form-error">{error}</p>}
        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={sending}
            className="w-full sm:w-auto bg-amber hover:bg-amberdark disabled:opacity-50 text-white font-display font-bold uppercase tracking-wide text-lg px-10 py-4 transition-colors"
            data-testid="lead-form-submit-button"
          >
            {sending ? "Odesíláme…" : "Chci návrh mise"}
          </button>
          <p className="text-xs text-dim mt-3">Odesláním souhlasíte se zpracováním údajů dle zásad ochrany osobních údajů. Návrh mise je nezávazný.</p>
        </div>
      </form>
    </div>
  );
};

export const Faq = ({ items, heading = "Časté otázky" }) => (
  <div data-testid="faq-accordion">
    <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white">{heading}</h2>
    <div className="mt-6 divide-y divide-line border border-line">
      {items.map((f, i) => (
        <details key={i} className="group bg-surface" data-testid={`faq-accordion-item-${i + 1}`}>
          <summary className="flex items-center justify-between gap-4 px-5 py-4 text-left">
            <span className="font-semibold text-white text-sm sm:text-base">{f.q}</span>
            <ChevronDown className="faq-chevron w-4 h-4 text-amber shrink-0 transition-transform" aria-hidden="true" />
          </summary>
          <div className="px-5 pb-5 text-sm text-mist leading-relaxed">{f.a}</div>
        </details>
      ))}
    </div>
  </div>
);
